import { withAuth } from '@microsoft/portal-app/lib/auth';
import { AnyPayload } from '@microsoft/portal-app/lib/redux/AnyPayload';
import { errorHandler } from '@microsoft/portal-app/lib/redux/observableErrorHandler';
import { FluxStandardAction } from 'flux-standard-action';
import { Epic } from 'redux-observable';
import { Observable } from 'rxjs/Observable';
import { ajax } from 'rxjs/observable/dom/ajax';
import { catchError, map } from 'rxjs/operators';
import { mergeMap } from 'rxjs/operators/mergeMap';
import { getDisableDevicesApiUrl } from '../../../redux/modules/accountApis';
import { IDevice } from '../../models';
import { IDevicesState, IRootDevicesState } from '../../models/IDevicesState';
import { DevicesActions } from '../DevicesActions';
import { devicesRegistry as registry } from '../devicesRegistry';
import { IDevicesAction } from '../IDevicesAction';

const withAuthAjax = withAuth(ajax);

export const disableDeviceEpic: Epic<FluxStandardAction<{ objectId: string, okFunction?: VoidFunction }>, IRootDevicesState> = (
  action
): Observable<FluxStandardAction<AnyPayload>> => {
  return action.ofType(DevicesActions.disableDevice).pipe(
    map(({ payload }) => payload),
    mergeMap(payload => disableDeviceAjax(payload!.objectId, payload!.okFunction).mapTo(payload!.objectId)),
    map(objectId => ({
      type: DevicesActions.disableDeviceResponse,
      payload: { objectId }
    })),
    catchError((err, parent) => {
      return parent.mergeMap(_action => errorHandler(DevicesActions.disableDeviceFailed, _action)(err));
    })
  );
};
registry.addEpic(DevicesActions.disableDevice, disableDeviceEpic);

// Immediately show the device as disabled (to be re-enabled if the disable
// request fails).
export const disableDevice = (
  state: IDevicesState,
  { payload }: IDevicesAction<{ objectId: string }>
): IDevicesState => {
  return updateDevice(state, payload!.objectId, { accountEnabled: false });
};

registry.add(DevicesActions.disableDeviceResponse, disableDevice);

// Re-enable the device if the disable request fails.
export const disableDeviceFailed = (
  state: IDevicesState,
  { meta }: FluxStandardAction<AnyPayload, { parent: IDevicesAction<{ objectId: string }> }>
) => {
  return updateDevice(state, meta!.parent.payload!.objectId, { accountEnabled: true });
};
registry.add(DevicesActions.disableDeviceFailed, disableDeviceFailed);

const disableDeviceAjax = (objectId: string, okFunction?: VoidFunction) =>
  withAuthAjax.postJSON({
    url: getDisableDevicesApiUrl(objectId)
  }).finally(() => {
    if (okFunction) {
      okFunction();
    }
  });

const updateDevice = (state: IDevicesState, objectId: string, updates: Partial<IDevice>): IDevicesState => {
  const newDeviceByIdMap = new Map<string, IDevice>();
  state.devicesById.forEach((value, key) => {
    // The devicesById map uses deviceId instead of id(objectId) as the key
    // Instead, we need to iterate each value and check objectId
    if (value.id === objectId) {
      newDeviceByIdMap.set(key, { ...value, ...updates });
    } else {
      newDeviceByIdMap.set(key, value);
    }
  });
  return {
    ...state,
    devicesById: newDeviceByIdMap
  };
};
